import React from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { Crumb } from "@repoflow/components/breadcrumb";
import { Nav } from "@repoflow/components/nav";
import * as Section from "@repoflow/components/section";
import BlogPostPublicSchema from "@common/schema/blog-post/public";
import * as PostsRoute from "./posts";
import * as CollectionsRoute from "./collections";
import * as AuthorsRoute from "./authors";
import * as TemplatesRoute from "./templates";

// THE BLOG, beneath a site: posts, collections, authors and templates, each a
// tab.
//
// Gen-2 had a Configuration tab holding the three templates and the preview
// settings (archive/recovered/stack-env/code/site-frontend/src/routes/domain/
// blog/config). The templates were Sections for a while — edited on the
// Sections tab with the section editor — and are a tab of their own here
// again, for the reasons @common/schema/site/blog gives; the preview settings
// are the template editor's own pane. See docs/pagews-blog.md §3.
const LocalProvider = ({ match: { params }, children: Children }) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/blog`;
  const basepath = `${parentBasepath}/blog`;

  return (
    <Section.Provider
      schema={BlogPostPublicSchema}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath }) => (
        <>
          <Tabs base={base} basepath={basepath} />
          <Switch>
            <Route path={`${basepath}/post`} component={PostsRoute.Content} />
            <Route path={`${basepath}/collection`} component={CollectionsRoute.Content} />
            <Route path={`${basepath}/author`} component={AuthorsRoute.Content} />
            <Route path={`${basepath}/template`} component={TemplatesRoute.Content} />
            <Redirect to={`${base}/post`} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

// The blog's tabs, hidden inside a template's editor. The site's own bar
// steps aside there (routes/sites/site) for the width the editor wants and so
// that the site's navigation is not a click away from the editor's actions;
// this bar sitting between the breadcrumb and the editor was the same hazard
// one level down. The breadcrumb is the way back to the list.
function Tabs({ base, basepath }) {
  const insideTemplate = useRouteMatch({
    path: `${basepath}/template/:kind(post|list|collection)`,
  });
  if (insideTemplate) return null;

  return (
    <Container>
      <Row>
        <Col>
          <Nav
            id="blog-tabs"
            tabs
            sections={[
              { label: "Posts", route: `${base}/post` },
              { label: "Collections", route: `${base}/collection` },
              { label: "Authors", route: `${base}/author` },
              { label: "Templates", route: `${base}/template` },
            ]}
          />
        </Col>
      </Row>
    </Container>
  );
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath }) => (
        <>
          <Crumb to={`${base}/post`} label={"Blog"} />
          <Switch>
            <Route path={`${basepath}/post`} component={PostsRoute.Indice} />
            <Route path={`${basepath}/collection`} component={CollectionsRoute.Indice} />
            <Route path={`${basepath}/author`} component={AuthorsRoute.Indice} />
            <Route path={`${basepath}/template`} component={TemplatesRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => null;
